import { getDocs, collection, query, orderBy, doc, getDoc, deleteDoc, addDoc, updateDoc, serverTimestamp, where } from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchCompaniesData } from '../../../companies/service/companiesService';

export type BAMSHardware = {
    id: string;
    bankId?: string;
    bankName?: string;
    branchId?: string;
    branchCode?: string;
    branchName?: string;
    region?: string;
    hardwareType?: 'BAMS';
    vendorName?: string;
    response?: 'Police' | 'QRF';
    noOfBamsSystem?: number;
    fixedPanicButton?: number;
    remotePanic?: number;
    fixedFootPanic?: number;
    pir?: number;
    openingDoorContact?: number;
    smokeHeatDetectors?: number;
    noHooterSounderBranch?: number;
    noOfKeypad?: number;
    cablesWires?: number;
    other?: number;
    installationDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
    installedBy?: string;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    companyId?: string;
    [key: string]: any;
};

export type BAMSHardwareFormData = {
    branchId?: string;
    region?: string;
    vendorName?: string;
    response?: 'Police' | 'QRF';
    noOfBamsSystem?: number;
    fixedPanicButton?: number;
    remotePanic?: number;
    fixedFootPanic?: number;
    pir?: number;
    openingDoorContact?: number;
    smokeHeatDetectors?: number;
    noHooterSounderBranch?: number;
    noOfKeypad?: number;
    cablesWires?: number;
    other?: number;
    installationDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
};

/**
 * Fetch company ID for the current user (PSO or Bank user)
 */
export async function fetchCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    try {
        if (isBankUser) {
            // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
            const directBank = await getDoc(doc(db, "banks", uid));
            if (directBank.exists()) {
                return uid;
            }
            // Try query by userId field
            const banksSnap = await getDocs(collection(db, "banks"));
            for (const bankDoc of banksSnap.docs) {
                const bankData = bankDoc.data();
                if (bankData.userId === uid) {
                    return bankDoc.id;
                }
            }
            return null;
        } else {
            // For PSO users, find which bank has this PSO
            const banksSnap = await getDocs(collection(db, 'banks'));
            for (const bankDoc of banksSnap.docs) {
                const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
                const posDoc = await getDoc(posDocRef);
                if (posDoc.exists()) {
                    return bankDoc.id;
                }
            }
            return null;
        }
    } catch (error) {
        console.error('Error fetching company ID:', error);
        return null;
    }
}

/**
 * Fetch all BAMS hardware installations for a company
 */
export async function fetchBAMSHardware(companyId: string): Promise<BAMSHardware[]> {
    if (!companyId) {
        console.log('No company ID provided');
        return [];
    }

    try {
        const hardwareSnap = await getDocs(
            query(
                collection(db, "banks", companyId, "bams_hardware"),
                orderBy("createdAt", "desc")
            )
        );
        
        const hardwareList = hardwareSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as BAMSHardware[];

        return hardwareList;
    } catch (error) {
        console.error('Error fetching BAMS hardware:', error);
        throw error;
    }
}

/**
 * Create a new BAMS hardware installation
 */
export async function createBAMSHardware(
    companyId: string,
    hardwareData: Partial<BAMSHardware>
): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid || !companyId) {
        throw new Error('User ID or company ID not found');
    }

    try {
        // Fetch bank and branch details
        const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', hardwareData.branchId || ''));
        const branchData = branchDoc.exists() ? branchDoc.data() : {};
        
        // Fetch location name if locationId exists
        let locationName = '';
        if (branchData.locationId) {
            try {
                const locationDoc = await getDoc(doc(db, 'locations', branchData.locationId));
                if (locationDoc.exists()) {
                    locationName = locationDoc.data().name || '';
                }
            } catch (err) {
                console.error('Error fetching location:', err);
            }
        }
        
        const bankDoc = await getDoc(doc(db, 'banks', companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};

        const newHardwareData = {
            ...hardwareData,
            hardwareType: 'BAMS',
            bankName: bankData.companyName || bankData.name || 'N/A',
            branchCode: branchData.branchCode || 'N/A',
            branchName: branchData.address || 'N/A',
            region: locationName || hardwareData.region || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: uid,
            companyId: companyId,
        };

        const docRef = await addDoc(collection(db, 'banks', companyId, 'bams_hardware'), newHardwareData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating BAMS hardware:', error);
        throw error;
    }
}

/**
 * Update an existing BAMS hardware installation
 */
export async function updateBAMSHardware(
    companyId: string,
    hardwareId: string,
    updateData: Partial<BAMSHardware>
): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        // If branch changed, update branch details
        if (updateData.branchId) {
            const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', updateData.branchId));
            const branchData = branchDoc.exists() ? branchDoc.data() : {};
            updateData.branchCode = branchData.branchCode || updateData.branchCode;
            updateData.branchName = branchData.address || updateData.branchName;
            
            // Fetch location name if locationId exists
            if (branchData.locationId) {
                try {
                    const locationDoc = await getDoc(doc(db, 'locations', branchData.locationId));
                    if (locationDoc.exists()) {
                        updateData.region = locationDoc.data().name || updateData.region || '';
                    }
                } catch (err) {
                    console.error('Error fetching location:', err);
                }
            }
        }

        await updateDoc(doc(db, 'banks', companyId, 'bams_hardware', hardwareId), {
            ...updateData,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating BAMS hardware:', error);
        throw error;
    }
}

/**
 * Delete a BAMS hardware installation
 */
export async function deleteBAMSHardware(companyId: string, hardwareId: string): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        await deleteDoc(doc(db, 'banks', companyId, 'bams_hardware', hardwareId));
    } catch (error) {
        console.error('Error deleting BAMS hardware:', error);
        throw error;
    }
}

/**
 * Fetch branches for a company
 */
export async function fetchBranches(companyId: string): Promise<any[]> {
    try {
        // Fetch locations
        const locSnap = await getDocs(collection(db, "locations"));
        const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

        const branchesSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
        return branchesSnap.docs.map(doc => {
            const branchData = doc.data();
            return {
                id: doc.id,
                ...branchData,
                locationName: locList.find(l => l.id === branchData.locationId)?.name || "N/A"
            };
        });
    } catch (error) {
        console.error('Error fetching branches:', error);
        return [];
    }
}

/**
 * Fetch deployments for a company to get BAMS vendor names
 * Only returns vendors where company type is "BAMS"
 * @param companyId - The bank/company ID
 * @param uid - User ID (used to find bank context for fetching companies)
 * @param fetchAllDeployments - If true, fetch all deployments regardless of PSO (for vendor display)
 */
export async function fetchDeployments(companyId: string, uid: string, fetchAllDeployments: boolean = false): Promise<Map<string, string>> {
    try {
        // Fetch deployments from banks/{companyId}/companyDeployment
        const deploymentsSnap = await getDocs(collection(db, "banks", companyId, "companyDeployment"));
        const deployments = deploymentsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as any[];

        // If fetchAllDeployments is true, don't filter by PSO (to show vendors for all branches)
        // Otherwise, filter by PSO ID (same as deploymentsService)
        let filteredDeployments = deployments;
        if (!fetchAllDeployments) {
            filteredDeployments = deployments.filter(deployment => deployment.psoId === uid);
        }

        // Fetch companies using the same method as deploymentsService
        // Use the companyId to determine if we should fetch as bank user
        const bankDoc = await getDoc(doc(db, "banks", companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};
        const isBankUserForCompanies = bankData.userId === uid || companyId === uid;
        const companies = await fetchCompaniesData(uid, isBankUserForCompanies);

        // Filter companies to only include BAMS companies
        const bamsCompanies = companies.filter(company => company.companyType === 'BAMS');

        console.log('BAMS fetchDeployments - Total deployments:', deployments.length);
        console.log('BAMS fetchDeployments - Filtered deployments:', filteredDeployments.length);
        console.log('BAMS fetchDeployments - Total companies:', companies.length);
        console.log('BAMS fetchDeployments - BAMS companies:', bamsCompanies.length);
        console.log('BAMS fetchDeployments - BAMS Companies IDs:', bamsCompanies.map(c => c.id));
        console.log('BAMS fetchDeployments - Deployments:', filteredDeployments.map(d => ({ branchId: d.branchId, companiesId: d.companiesId })));

        // Create a map: branchId -> companyName (vendor name) - only for BAMS companies
        const vendorMap = new Map<string, string>();

        filteredDeployments.forEach(deployment => {
            if (deployment.branchId && deployment.companiesId) {
                // Only match if the company is a BAMS company
                const company = bamsCompanies.find(c => c.id === deployment.companiesId);
                if (company && company.companyName) {
                    // Use the enriched company name (already merged from global if available)
                    vendorMap.set(deployment.branchId, company.companyName);
                    console.log(`BAMS fetchDeployments - Matched branch ${deployment.branchId} to BAMS vendor: ${company.companyName}`);
                } else {
                    console.log(`BAMS fetchDeployments - No BAMS company found for deployment: branchId=${deployment.branchId}, companiesId=${deployment.companiesId}`);
                }
            }
        });

        console.log('BAMS fetchDeployments - Final vendorMap:', Array.from(vendorMap.entries()));
        return vendorMap;
    } catch (error) {
        console.error('Error fetching deployments:', error);
        return new Map();
    }
}

